import Vue from 'vue';
import '@mdi/font/css/materialdesignicons.css';
import '@/amplify/fetch-interceptor';
import '@/amplify';
import '@aws-amplify/ui-vue';
import '@/plugins/vue-patch';

import VueTelInputVuetify from 'vue-tel-input-vuetify/lib';
import ElementUI from 'element-ui';
import LottieAnimation from 'lottie-web-vue';

import locale from 'element-ui/lib/locale/lang/en';
import router from '@/router';
import store from '@/store';
import i18n from '@/i18n';
import vuetify from '@/plugins/vuetify';
import clipboard from '@/plugins/clipboard';
import '@/plugins/prefixedTranslations';
import App from '@/App.vue';
import '@/assets/styles/element-variables.scss';
import Vidle from 'v-idle';

Vue.use(Vidle);
Vue.use(VueTelInputVuetify, {
  vuetify,
});
Vue.use(ElementUI, { locale });
Vue.use(LottieAnimation);
Vue.config.productionTip = false;

// Lazy route/CSS chunks 404 after a new deploy while an old tab is still open,
// surfacing as a white error screen. Hard-reload once (guarded so it can't loop)
// to pull the current build. The guard is cleared on a successful boot below.
function handleChunkLoadError(error) {
  const message = `${(error && (error.message || error.name)) || error}`;
  if (/ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed/i.test(message)) {
    if (!sessionStorage.getItem('chunk-reload-attempted')) {
      sessionStorage.setItem('chunk-reload-attempted', '1');
      window.location.reload();
    }
  }
}
window.addEventListener('error', event => handleChunkLoadError(event.error), true);
window.addEventListener('unhandledrejection', event => handleChunkLoadError(event.reason));

new Vue({
  router,
  store,
  i18n,
  vuetify,
  clipboard,
  render: h => h(App),
}).$mount('#app');

// Reveal the app only after the text AND icon fonts have loaded, so the boot
// skeleton (in index.html) covers the font-swap that used to flash the sidebar
// labels and icons on hard refresh. Icon fonts load lazily (only when a glyph
// first renders), so document.fonts.ready alone can resolve before they are even
// requested — we force-load the critical families explicitly and wait for those.
// A timeout guards against a font that never resolves so the app always reveals.
const CRITICAL_FONTS = [
  '400 1em "Inter-Regular"',
  '500 1em "Inter-Medium"',
  '400 24px "Material Icons"', // Vuetify v-icon (iconfont: 'md') — the sidebar icons
  '400 24px "Material Icons Outlined"',
  '400 24px "Material Icons Round"',
  '400 24px "Material Design Icons"', // @mdi/font (mdi-* classes)
];

function loadCriticalFonts() {
  if (!document.fonts || !document.fonts.load) return Promise.resolve();
  return Promise.all(
    CRITICAL_FONTS.map(spec => document.fonts.load(spec).catch(() => {})),
  ).then(() => document.fonts.ready);
}

// The app shell has painted: the initial route resolved (onReady), Vue flushed
// its render (nextTick), and the browser painted it (two animation frames). This
// is what closes the "empty sidebar then icons appear" gap — we hold the skeleton
// until the real sidebar is on screen, not merely until fonts are ready.
function shellPainted() {
  return new Promise(resolve => {
    router.onReady(() => {
      Vue.nextTick(() => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    });
  });
}

function revealApp() {
  document.documentElement.classList.add('app-fonts-ready');
  // Booted cleanly — re-arm the chunk-error reload guard for future deploys.
  try { sessionStorage.removeItem('chunk-reload-attempted'); } catch (e) { /* noop */ }
  const app = document.getElementById('app');
  if (app) app.removeAttribute('aria-busy');
  const skeleton = document.getElementById('app-skeleton');
  if (!skeleton) return;
  skeleton.classList.add('app-skeleton--hidden');
  setTimeout(() => skeleton.remove(), 450);
}

// Reveal once fonts are decoded AND the shell has painted, but never before a
// short minimum (so a fast cached load doesn't flash-and-vanish the skeleton) and
// never later than a safety cap (so a hung route/font can't trap the skeleton).
const MIN_SKELETON_MS = 200;
const SAFETY_MS = 4000;
const minVisible = new Promise(resolve => { setTimeout(resolve, MIN_SKELETON_MS); });
const safetyCap = new Promise(resolve => { setTimeout(resolve, SAFETY_MS); });
Promise.race([
  Promise.all([loadCriticalFonts(), shellPainted(), minVisible]),
  safetyCap,
]).then(revealApp);
